import React, { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography
} from '@mui/material';
import DeleteRoundedIcon      from '@mui/icons-material/DeleteRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import FilterListRoundedIcon   from '@mui/icons-material/FilterListRounded';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import adminQueueService from '../../../../../services/adminQueueService';

function TicketManagement() {
	const { t } = useTranslation();

	const STATUS_OPTIONS = [
		{ value: '',           label: t('admin.tickets.allStatuses') },
		{ value: 'WAITING',    label: t('admin.tickets.status.WAITING') },
		{ value: 'CALLED',     label: t('admin.tickets.status.CALLED') },
		{ value: 'DONE',       label: t('admin.tickets.status.DONE') },
		{ value: 'CANCELLED',  label: t('admin.tickets.status.CANCELLED') },
	];

	const STATUS_CHIP = {
		WAITING:   { label: t('admin.tickets.status.WAITING'),   color: 'warning' },
		CALLED:    { label: t('admin.tickets.status.CALLED'),    color: 'info' },
		DONE:      { label: t('admin.tickets.status.DONE'),      color: 'success' },
		CANCELLED: { label: t('admin.tickets.status.CANCELLED'), color: 'default' },
	};

	const [tickets, setTickets]             = useState([]);
	const [totalElements, setTotalElements] = useState(0);
	const [loading, setLoading]             = useState(false);
	const [page, setPage]                   = useState(0);
	const rowsPerPage = 20;

	const [managers, setManagers]           = useState([]);
	const [serviceTypes, setServiceTypes]   = useState([]);

	const [filterStatus, setFilterStatus]           = useState('');
	const [filterManager, setFilterManager]         = useState('');
	const [filterServiceType, setFilterServiceType] = useState('');

	const [deleteId, setDeleteId] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const fetchTickets = useCallback(async (p = page) => {
		setLoading(true);
		try {
			const res = await adminQueueService.getAllTicketsAdmin({
				status:        filterStatus        || undefined,
				managerId:     filterManager       || undefined,
				serviceTypeId: filterServiceType   || undefined,
				page: p,
				size: rowsPerPage,
			});
			setTickets(res.data.content || []);
			setTotalElements(res.data.page?.totalElements ?? res.data.totalElements ?? 0);
		} catch {
			toast.error(t('admin.tickets.loadError'));
		} finally {
			setLoading(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, filterStatus, filterManager, filterServiceType]);

	useEffect(() => {
		adminQueueService.getAllManagers(0, 200)
			.then(r => setManagers(r.data.content || []))
			.catch(() => {});
		adminQueueService.getAllServiceTypes(0, 200)
			.then(r => setServiceTypes(r.data.content || []))
			.catch(() => {});
	}, []);

	useEffect(() => {
		fetchTickets(page);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, filterStatus, filterManager, filterServiceType]);

	const handleDeleteConfirm = async () => {
		setDeleting(true);
		try {
			await adminQueueService.deleteTicketAdmin(deleteId);
			toast.success(t('admin.tickets.deletedSuccess'));
			setDeleteId(null);
			fetchTickets(page);
		} catch (e) {
			toast.error(e?.response?.data?.message || t('admin.tickets.deletedError'));
		} finally {
			setDeleting(false);
		}
	};

	const handleExportCsv = () => {
		if (tickets.length === 0) return;
		const headers = [
			t('admin.tickets.colTicket'), t('admin.tickets.colName'),
			t('admin.tickets.colPhone'),
			t('admin.tickets.colServiceType'), t('admin.tickets.colManager'),
			t('admin.tickets.colWindow'), t('admin.tickets.colStatus'),
			t('admin.tickets.colCreated'),
		];
		const rows = tickets.map(ticket => [
			ticket.ticketNumber  || '',
			ticket.fullName      || '',
			ticket.phoneNumber   || '',
			ticket.serviceTypeName || '',
			ticket.managerName   || '',
			ticket.windowNumber  || '',
			STATUS_CHIP[ticket.ticketStatus]?.label || ticket.ticketStatus || '',
			ticket.createdDate ? new Date(ticket.createdDate).toLocaleString('ru-RU') : '',
		]);
		const csv = [headers, ...rows]
			.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
			.join('\n');
		const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
		const url  = URL.createObjectURL(blob);
		const a    = document.createElement('a');
		a.href     = url;
		a.download = `tickets_${new Date().toISOString().slice(0, 10)}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const hasFilters = filterStatus || filterManager || filterServiceType;

	return (
		<Box>
			{/* ── Заголовок ─────────────────────────────────────────── */}
			<Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2.5 }} flexWrap='wrap' gap={1}>
				<Typography variant='h6' sx={{ fontWeight: 700 }}>
					{t('admin.tickets.title')}{totalElements > 0 && ` (${totalElements})`}
				</Typography>
				<Button
					variant='outlined'
					startIcon={<FileDownloadRoundedIcon />}
					onClick={handleExportCsv}
					disabled={tickets.length === 0}
					sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
				>
					{t('admin.tickets.exportCsv')}
				</Button>
			</Stack>

			{/* ── Фильтры ───────────────────────────────────────────── */}
			<Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)' }}>
				<Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap' useFlexGap>
					<FilterListRoundedIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />

					<FormControl size='small' sx={{ minWidth: 160 }}>
						<InputLabel>{t('admin.tickets.filterStatus')}</InputLabel>
						<Select
							value={filterStatus}
							label={t('admin.tickets.filterStatus')}
							onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
						>
							{STATUS_OPTIONS.map(o => (
								<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size='small' sx={{ minWidth: 200 }}>
						<InputLabel>{t('admin.tickets.filterManager')}</InputLabel>
						<Select
							value={filterManager}
							label={t('admin.tickets.filterManager')}
							onChange={e => { setFilterManager(e.target.value); setPage(0); }}
						>
							<MenuItem value=''>{t('admin.tickets.allManagers')}</MenuItem>
							{managers.map(m => (
								<MenuItem key={m.id} value={m.id}>
									{m.surname} {m.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size='small' sx={{ minWidth: 200 }}>
						<InputLabel>{t('admin.tickets.filterServiceType')}</InputLabel>
						<Select
							value={filterServiceType}
							label={t('admin.tickets.filterServiceType')}
							onChange={e => { setFilterServiceType(e.target.value); setPage(0); }}
						>
							<MenuItem value=''>{t('admin.tickets.allTypes')}</MenuItem>
							{serviceTypes.map(st => (
								<MenuItem key={st.id} value={st.id}>{st.nameRu}</MenuItem>
							))}
						</Select>
					</FormControl>

					{hasFilters && (
						<Button
							size='small'
							onClick={() => { setFilterStatus(''); setFilterManager(''); setFilterServiceType(''); setPage(0); }}
							sx={{ textTransform: 'none', color: 'text.secondary' }}
						>
							{t('admin.tickets.resetFilters')}
						</Button>
					)}
				</Stack>
			</Paper>

			{/* ── Таблица ───────────────────────────────────────────── */}
			<Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden' }}>
				<TableContainer>
					<Table size='small'>
						<TableHead>
							<TableRow sx={{ backgroundColor: 'rgba(25,118,210,0.06)' }}>
								<TableCell sx={{ fontWeight: 700, width: 40 }}>№</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.tickets.colTicket')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.tickets.colName')}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t('admin.tickets.colPhone')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.tickets.colServiceType')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.tickets.colManager')}</TableCell>
								<TableCell sx={{ fontWeight: 700, width: 70 }}>{t('admin.tickets.colWindow')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.tickets.colStatus')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.tickets.colCreated')}</TableCell>
								<TableCell sx={{ fontWeight: 700, textAlign: 'right', width: 60 }}>{t('admin.tickets.colDelete')}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={10} align='center' sx={{ py: 5 }}>
										<CircularProgress size={32} />
									</TableCell>
								</TableRow>
							) : tickets.length === 0 ? (
								<TableRow>
									<TableCell colSpan={10} align='center' sx={{ py: 5, color: 'text.secondary' }}>
										{t('admin.tickets.empty')}
									</TableCell>
								</TableRow>
							) : (
								tickets.map((ticket, idx) => {
									const chip = STATUS_CHIP[ticket.ticketStatus] || { label: ticket.ticketStatus, color: 'default' };
									return (
										<TableRow key={ticket.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
											<TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
												{page * rowsPerPage + idx + 1}
											</TableCell>
											<TableCell>
												<Typography sx={{ fontWeight: 800, fontFamily: '"Roboto Mono", monospace', fontSize: 14 }}>
													{ticket.ticketNumber}
												</Typography>
											</TableCell>
											<TableCell sx={{ fontSize: 13 }}>{ticket.fullName || '—'}</TableCell>
														<TableCell sx={{ fontSize: 13 }}>{ticket.phoneNumber || '—'}</TableCell>
											<TableCell>
												{ticket.serviceTypeName ? (
													<Chip
														label={ticket.serviceTypeName}
														size='small'
														sx={{ backgroundColor: ticket.serviceTypeColor || '#EDE9FE', fontWeight: 600, fontSize: 11 }}
													/>
												) : '—'}
											</TableCell>
											<TableCell sx={{ fontSize: 13 }}>{ticket.managerName || '—'}</TableCell>
											<TableCell>
												{ticket.windowNumber
													? <Chip label={`№${ticket.windowNumber}`} size='small' variant='outlined' color='primary' sx={{ fontWeight: 700 }} />
													: '—'}
											</TableCell>
											<TableCell>
												<Chip label={chip.label} size='small' color={chip.color} />
											</TableCell>
											<TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
												{ticket.createdDate
													? new Date(ticket.createdDate).toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })
													: '—'}
											</TableCell>
											<TableCell align='right'>
												<Tooltip title={t('admin.tickets.tooltipDelete')}>
													<IconButton size='small' color='error' onClick={() => setDeleteId(ticket.id)}>
														<DeleteRoundedIcon fontSize='small' />
													</IconButton>
												</Tooltip>
											</TableCell>
										</TableRow>
									);
								})
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					component='div'
					count={totalElements}
					page={page}
					onPageChange={(_, p) => setPage(p)}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[20]}
					labelDisplayedRows={({ from, to, count }) => t('common.from_to_count', { from, to, count })}
				/>
			</Paper>

			{/* ── Диалог удаления ───────────────────────────────────── */}
			<Dialog open={!!deleteId} onClose={() => !deleting && setDeleteId(null)} maxWidth='xs' fullWidth>
				<DialogTitle sx={{ fontWeight: 700 }}>{t('admin.tickets.deleteTitle')}</DialogTitle>
				<DialogContent>
					<Typography variant='body2' color='text.secondary'>
						{t('admin.tickets.deleteBody')}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
					<Button onClick={() => setDeleteId(null)} disabled={deleting} sx={{ textTransform: 'none', borderRadius: 2 }}>
						{t('common.cancel')}
					</Button>
					<Button
						variant='contained'
						color='error'
						onClick={handleDeleteConfirm}
						disabled={deleting}
						sx={{ textTransform: 'none', borderRadius: 2, minWidth: 100 }}
					>
						{deleting ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : t('common.delete')}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default TicketManagement;
